// Fiche d'un bon d'intervention, pensée pour le TÉLÉPHONE : sections repliables, barre d'action collante (Enregistrer, Faire signer,
// PDF) toujours visible, cibles de 44 px. Bon signé : tout passe en lecture seule (le serveur et la base le refusent de toute façon) ;
// une correction = un avenant (encadrement).
// SANS RÉSEAU (31/10/2026, migration 047) : un bon ouvert est gardé sur l'appareil (lisible et modifiable sans réseau) ; « Enregistrer »
// range alors la modification dans la file d'envoi du téléphone (lib/fileEnvoi.ts, la même que le carnet de chantier), rejouée seule
// au retour du réseau, sans doublon ; si un collègue a modifié le bon entre-temps, rien n'est écrasé et la fiche le dit (reprendre ou
// abandonner ses modifications). La signature, le PDF, les photos et les sauvegardes exigent le réseau : désactivés, avec la raison.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, CloudUpload, FileDown, FilePlus2, FileSignature, FlaskConical, MessageSquarePlus, Plus, RotateCcw, Save, ShieldCheck, Trash2, Upload, X } from 'lucide-react';
import {
  ACCES_POINT, DOE_PROPOSES, FAMILLES_FLUIDE, LIBELLES_ACCES_POINT, LIBELLES_FAMILLE_FLUIDE, LIBELLES_NATURE_FLUIDE, LIBELLES_RESULTAT,
  LIBELLES_TYPE_BON, NATURES_INTERVENTION_FLUIDE, PROTOCOLES, RESULTATS_TEST, TYPES_BON,
  cleSerie, type FluidesBon, type ResultatTest, type SectionsBon, type TraceSignature, type TypeBon,
  LIBELLES_SENS_AJUSTEMENT, RAISON_AJUSTEMENT_MAX, SENS_AJUSTEMENT, centimesMontant, centimesSignes, formatMontantSigne, libelleDevis, totalNetCentimes,
  type AjustementDevis, type SensAjustement,
} from '@shared/bons-intervention.ts';
import { api, ErreurApi, messageErreur } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { useModulesEtat } from '../../lib/modules.ts';
import { useEnLigne } from '../../lib/preferences.ts';
import { rendreDisponible } from '../../lib/horsLigne.ts';
import { abandonner, nouvelIdentifiant, opsDuBon, rangerBon, synchroniser, useFileEnvoi, verdictDe, type OperationFile } from '../../lib/fileEnvoi.ts';
import { date, dateHeure, pluriel } from '../../lib/format.ts';
import {
  Alerte, Badge, Bouton, Chargement, Champ, ChampChoix, ChampTexte, Dialogue, EntetePage, EtatErreur, useConfirmation, useNotifier,
} from '../../components/ui.tsx';
import { PageIntrouvable } from '../legal/PageIntrouvable.tsx';
import { BadgeStatutBon } from './PageBons.tsx';
import { SignatureFigee, ZoneSignature } from './Signature.tsx';
import type { Bon, FichierBon, Liens, Referentiels, SerieGamme } from './types.ts';
import { raisonNoteBloquee, raisonSignatureBloquee } from './hors-ligne.ts';
import { ChampIntervenantFluides } from './fluides.tsx';

export function PageBon() {
  const { charge, modules } = useModulesEtat();
  const { peut } = useSession();
  const { id = '' } = useParams();
  const q = useQuery({ queryKey: ['bon', id], queryFn: () => api.get<Bon>(`/api/bons/${id}`), enabled: charge && !!modules.bons_intervention });
  if (!charge) return <Chargement />;
  if (!modules.bons_intervention || !peut('bons.lire')) return <PageIntrouvable />;
  if (q.isLoading) return <Chargement libelle="Ouverture du bon…" />;
  if (q.isError || !q.data) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  return <FicheBon key={q.data.id} bon={q.data} />;
}

// ─── Formulaire ───
interface Formulaire {
  titre: string; type: TypeBon; demandeur: string; objet: string; techniciens: string[]; arrivee: string; depart: string;
  equipmentId: string; interventionId: string; chantierId: string; devisRevisionId: string;
  travaux: string; observations: string; suiteADonner: string; sections: SectionsBon;
}
/** ISO → valeur d'un champ « date et heure » (heure locale de l'appareil). */
const versLocal = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso); const z = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}T${z(d.getHours())}:${z(d.getMinutes())}`;
};
const versIso = (v: string) => (v ? new Date(v).toISOString() : null);
const formDepuis = (b: Bon): Formulaire => ({
  titre: b.titre, type: b.type, demandeur: b.demandeur ?? '', objet: b.objet ?? '', techniciens: b.techniciens.map((t) => t.id),
  arrivee: versLocal(b.arrivee), depart: versLocal(b.depart), equipmentId: b.equipment_id ?? '', interventionId: b.intervention_id ?? '', chantierId: b.chantier_id ?? '',
  devisRevisionId: b.devis_revision_id ?? '',
  travaux: b.travaux ?? '', observations: b.observations ?? '', suiteADonner: b.suite_a_donner ?? '',
  sections: {
    pieces: b.sections.pieces ?? [], passerelles: b.sections.passerelles ?? [], points: b.sections.points ?? [], essais: b.sections.essais ?? [],
    reserves: b.sections.reserves ?? null, doe: b.sections.doe ?? [], fluides: b.sections.fluides ?? null,
    ajustements_devis: b.sections.ajustements_devis ?? [],
  },
});
const fluidesVides = (): FluidesBon => ({
  operateur: { nom: null, adresse: null, siret: null, attestation_capacite: null }, detenteur: { nom: null, adresse: null, siret: null },
  equipement: { identification: null, fluide_id: null, fluide: null, famille: null, prg: null, prg_source: null, charge_totale_kg: null },
  natures: [], nature_autre: null, detecteur: { identification: null, date_controle: null }, detection_permanente: null,
  fuites: { constatees: null, lignes: [] }, charge: { vierge_kg: null, recycle_kg: null, regenere_kg: null },
  recupere: { traitement_kg: null, reutilisation_kg: null, contenant: null, bsff: null }, destination: null, observations: null,
});
const texteOuNull = (v: string) => (v.trim() ? v : null);
/** Corps de l'enregistrement (PATCH), sans la version de base : la même forme en ligne et dans la file d'envoi. */
const corpsDe = (f: Formulaire, bon: Bon) => ({
  titre: f.titre.trim() || bon.titre, type: f.type, demandeur: texteOuNull(f.demandeur), objet: texteOuNull(f.objet), techniciens: f.techniciens,
  arrivee: versIso(f.arrivee), depart: versIso(f.depart), equipmentId: f.equipmentId || null, interventionId: f.interventionId || null, chantierId: f.chantierId || null,
  devisRevisionId: f.devisRevisionId || null,
  travaux: texteOuNull(f.travaux), observations: texteOuNull(f.observations), suiteADonner: texteOuNull(f.suiteADonner), sections: f.sections,
});
const modifications = (n: number) => `${n} modification${n > 1 ? 's' : ''}`;

function Section({ titre, compte, ouvert, children, id }: { titre: string; compte?: ReactNode; ouvert?: boolean; children: ReactNode; id?: string }) {
  return (
    <details className="panneau bi-section" open={ouvert} id={id}>
      <summary className="bi-section-titre"><span>{titre}</span>{compte}</summary>
      <div className="panneau-corps pile">{children}</div>
    </details>
  );
}
const Compte = ({ n, un, plusieurs }: { n: number; un: string; plusieurs?: string }) => (n ? <Badge ton="info" icone={false}>{pluriel(n, un, plusieurs)}</Badge> : <Badge ton="neutre" icone={false}>Vide</Badge>);

function BoutonRetirer({ libelle, onClick }: { libelle: string; onClick: () => void }) {
  return <button type="button" className="btn btn-fantome btn-icone bi-retirer" aria-label={libelle} title={libelle} onClick={onClick}><X aria-hidden /></button>;
}

const ETAT_SERIE: Record<string, string> = { pose: 'posé', en_stock: 'en stock', retourne: 'retourné', hors_service: 'hors service' };
/** « Ma gamme : … » sous le champ N° de série quand le numéro ENREGISTRÉ figure dans Ma gamme (calculé par le serveur, vue publique). */
function SerieMaGamme({ bon, numero, lien }: { bon: Bon; numero: string | null; lien: boolean }) {
  const l: SerieGamme[] = numero ? bon.series_gamme?.[cleSerie(numero)] ?? [] : [];
  if (!l.length) return null;
  return (
    <ul className="bi-gamme petit">
      {l.map((x, i) => (
        <li key={`${x.produit.id}-${i}`}>
          <span>Ma gamme : {x.produit.designation} — <span className="mono">{x.produit.reference}</span>
            {x.date_pose ? `, posé le ${date(x.date_pose)}` : x.statut !== 'pose' && ETAT_SERIE[x.statut] ? `, ${ETAT_SERIE[x.statut]}` : ''}
            {x.site_nom ? ` sur ${x.site_nom}` : x.site_masque ? ' (site non autorisé pour votre compte)' : ''}</span>
          {lien && <> <Link to={`/gamme/${x.produit.id}?onglet=series`}>Voir la fiche produit</Link></>}
        </li>
      ))}
    </ul>
  );
}

function Verdict({ valeur, changer, nom }: { valeur: ResultatTest; changer: (v: ResultatTest) => void; nom: string }) {
  return (
    <div className="segment bi-verdict" role="group" aria-label={nom}>
      {RESULTATS_TEST.map((r) => (
        <button key={r} type="button" aria-pressed={valeur === r} className={`bi-verdict-${r}`} onClick={() => changer(r)}>{LIBELLES_RESULTAT[r]}</button>
      ))}
    </div>
  );
}

function FicheBon({ bon }: { bon: Bon }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const notifier = useNotifier();
  const { confirmer, element: confirmation } = useConfirmation();
  const enLigne = useEnLigne();
  // Enregistrements de CE bon gardés sur le téléphone (faits sans réseau) : en attente d'envoi, ou refusés par le serveur.
  const { proprio, enAttente, refusees } = useFileEnvoi();
  const attenteBon = opsDuBon(enAttente, bon.id);
  const refusBon = opsDuBon(refusees, bon.id);
  const derniere = attenteBon.at(-1);
  // Enregistrement qui vient de partir : montré jusqu'à ce que la fiche relue du serveur arrive (pas de retour fugitif à l'ancienne version).
  const [envoye, setEnvoye] = useState<{ majLe: string; formulaire: Formulaire } | null>(null);
  const precedente = useRef<OperationFile | undefined>(undefined);
  useEffect(() => {
    const p = precedente.current;
    if (p && p.clientOpId !== derniere?.clientOpId && !refusBon.some((o) => o.clientOpId === p.clientOpId)) {
      const v = verdictDe(p.clientOpId);
      if (v && (v.statut === 'acceptee' || v.statut === 'deja_recue')) setEnvoye({ majLe: bon.maj_le, formulaire: p.bon!.formulaire as Formulaire });
    }
    precedente.current = derniere;
  }, [derniere?.clientOpId, refusBon.length]);   // eslint-disable-line react-hooks/exhaustive-deps
  const referenceForm: Formulaire = (derniere?.bon?.formulaire as Formulaire | undefined)
    ?? (envoye && envoye.majLe === bon.maj_le ? envoye.formulaire : formDepuis(bon));
  const reference = JSON.stringify(referenceForm);
  const [f, setF] = useState<Formulaire>(() => formDepuis(bon));
  const modifie = JSON.stringify(f) !== reference;
  // Le bon a changé (côté serveur, ou enregistrement gardé sur le téléphone) : on reprend sa version si rien n'est en cours de saisie.
  const referencePrecedente = useRef(reference);
  useEffect(() => {
    const avant = referencePrecedente.current;
    referencePrecedente.current = reference;
    setF((x) => (JSON.stringify(x) === avant ? JSON.parse(reference) as Formulaire : x));
  }, [reference]);
  // Bon ouvert = gardé sur cet appareil (fiche, référentiels, état des options) pour le remplir sans réseau ; fiche rafraîchie à
  // chaque nouvelle version lue du serveur (même durée et même effacement à la déconnexion que le reste du hors connexion).
  useEffect(() => {
    if (!navigator.onLine) return;
    const url = `/api/bons/${bon.id}`;
    rendreDisponible({ cle: `bon:${bon.id}`, type: 'bon', libelle: `Bon ${bon.reference} — ${bon.titre}`, urls: [url, '/api/bons/referentiels', '/api/organisation/modules'] },
      { [url]: bon }).catch(() => { /* hors connexion indisponible sur ce navigateur : la fiche marche en ligne */ });
  }, [bon.maj_le]);   // eslint-disable-line react-hooks/exhaustive-deps
  const [signer, setSigner] = useState(false);
  const [avenant, setAvenant] = useState(false);
  const ecrire = bon.droits.ecrire;
  const { peut } = useSession();
  const lienGamme = peut('gamme.lire');
  const s = f.sections;
  const maj = (p: Partial<Formulaire>) => setF((x) => ({ ...x, ...p }));
  const majS = (p: Partial<SectionsBon>) => setF((x) => ({ ...x, sections: { ...x.sections, ...p } }));

  const referentiels = useQuery({ queryKey: ['bons-referentiels'], queryFn: () => api.get<Referentiels>('/api/bons/referentiels'), staleTime: 300_000 });
  const liens = useQuery({ queryKey: ['bons-liens', bon.site.id], queryFn: () => api.get<Liens>(`/api/bons/liens?site=${bon.site.id}`), enabled: ecrire });
  // Devis acceptés du site (051) : référence, intitulé, date ; le devis du chantier lié est proposé d'office (bouton).
  const devisSite = liens.data?.devis ?? [];
  const devisChantier = f.chantierId ? devisSite.find((x) => x.chantiers.includes(f.chantierId)) ?? null : null;
  const devisAffiche = f.devisRevisionId ? (devisSite.find((x) => x.revision_id === f.devisRevisionId) ?? (f.devisRevisionId === bon.devis_revision_id ? bon.devis : null)) : bon.statut === 'signe' ? bon.devis : null;
  const changerDevis = async (id: string) => {
    // Retirer le devis efface les ajouts / déductions saisis (ils n'ont de sens que par rapport à lui) : confirmation d'abord.
    if (!id && (f.sections.ajustements_devis ?? []).length) {
      if (!(await confirmer('Retirer le devis ?', 'Les ajouts et déductions saisis par rapport à ce devis seront retirés du bon.', 'Retirer le devis'))) return;
      setF((x) => ({ ...x, devisRevisionId: '', sections: { ...x.sections, ajustements_devis: [] } }));
      return;
    }
    maj({ devisRevisionId: id });
  };
  const intervenants = useQuery({ queryKey: ['bons-intervenants'], queryFn: () => api.get<{ id: string; nom: string }[]>('/api/bons/intervenants'), enabled: ecrire, staleTime: 300_000 });

  const rafraichir = (b: Bon) => { qc.setQueryData(['bon', b.id], b); qc.invalidateQueries({ queryKey: ['bons'] }); };
  // Enregistrer : en ligne, directement (identifiant d'envoi joint) ; sans réseau — ou derrière des enregistrements encore sur le
  // téléphone, pour garder l'ordre —, dans la file d'envoi. Coupure pendant l'envoi : la même saisie, sous le même identifiant, part
  // dans la file (si elle était arrivée, le serveur ne l'appliquera pas deux fois).
  const enregistrer = useMutation({
    // Sans réseau, la mutation part quand même (par défaut, React Query la mettrait en pause jusqu'au retour du réseau).
    networkMode: 'always',
    mutationFn: async (): Promise<{ bon: Bon } | { horsLigne: true }> => {
      const corps = corpsDe(f, bon);
      const clientOpId = nouvelIdentifiant();
      const ranger = () => rangerBon(proprio, { bonId: bon.id, reference: bon.reference, corps, majLe: bon.maj_le, formulaire: f, clientOpId });
      if (!navigator.onLine || attenteBon.length) {
        await ranger();
        if (navigator.onLine) { await synchroniser(proprio).catch(() => null); await qc.invalidateQueries({ queryKey: ['bon', bon.id] }); }
        return { horsLigne: true };
      }
      try {
        return { bon: await api.patch<Bon>(`/api/bons/${bon.id}`, { ...corps, majLe: bon.maj_le, clientOpId, occurredAt: new Date().toISOString() }) };
      } catch (e) {
        if (e instanceof ErreurApi && e.code === 'hors_connexion') { await ranger(); return { horsLigne: true }; }
        throw e;
      }
    },
    onSuccess: (r) => {
      if ('bon' in r) { setF(formDepuis(r.bon)); rafraichir(r.bon); notifier('Bon enregistré.'); return; }
      notifier(navigator.onLine ? 'Enregistrement envoyé au serveur.' : 'Enregistré sur ce téléphone : envoi automatique au retour du réseau.');
    },
    onError: (e) => notifier(messageErreur(e) + (e instanceof ErreurApi && e.details ? ` ${(e.details as any[]).map?.((x) => x.message).filter(Boolean).join(', ') ?? ''}` : ''), true),
  });
  const ouvrirSignature = async () => {
    if (modifie) { try { if ('horsLigne' in (await enregistrer.mutateAsync())) return; } catch { return; } }
    setSigner(true);
  };
  // Enregistrement refusé (bon modifié entre-temps par un collègue, signé ailleurs…) : rien n'a été écrasé ; la personne choisit.
  const reprendre = async () => {
    const dernier = refusBon.at(-1);
    if (!dernier?.bon) return;
    for (const o of refusBon) await abandonner(proprio, o.clientOpId).catch(() => {});
    await qc.invalidateQueries({ queryKey: ['bon', bon.id] });
    setF(dernier.bon.formulaire as Formulaire);
    notifier('Vos modifications sont reprises dans la fiche : vérifiez-les, puis enregistrez.');
  };
  const abandonnerTout = async () => {
    const n = refusBon.length;
    if (!(await confirmer('Abandonner vos modifications ?', `${modifications(n)} faite${n > 1 ? 's' : ''} sans réseau ne ${n > 1 ? 'seront' : 'sera'} pas envoyée${n > 1 ? 's' : ''} : la fiche garde la version du serveur.`, 'Abandonner'))) return;
    for (const o of refusBon) await abandonner(proprio, o.clientOpId).catch(() => {});
    await qc.invalidateQueries({ queryKey: ['bon', bon.id] });
    notifier('Modifications abandonnées.');
  };
  const envoyerMaintenant = async () => {
    const b = await synchroniser(proprio).catch(() => null);
    await qc.invalidateQueries({ queryKey: ['bon', bon.id] });
    if (b?.arret === 'reseau') notifier('Toujours pas de réseau : les modifications restent sur ce téléphone.', true);
  };
  const signatureBloquee = raisonSignatureBloquee({ enLigne, enAttente: attenteBon.length, refusees: refusBon.length });
  const supprimer = useMutation({
    mutationFn: () => api.del(`/api/bons/${bon.id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['bons'] }); notifier('Brouillon supprimé.'); nav('/interventions/bons'); },
    onError: (e) => notifier(messageErreur(e), true),
  });

  const nbFichiers = (n: 'photo' | 'sauvegarde') => bon.fichiers.filter((x) => x.nature === n).length;
  const tech = (id: string) => intervenants.data?.find((x) => x.id === id)?.nom ?? bon.techniciens.find((x) => x.id === id)?.nom ?? 'Compte inconnu';

  return (
    <div className="pile-l bi-fiche">
      <EntetePage
        fil={<nav className="petit"><Link to="/interventions/bons">Bons d’intervention</Link></nav>}
        titre={<>{bon.titre}</>}
        sousTitre={<span className="bi-entete-meta">
          <span className="mono">{bon.reference}{bon.version_no > 1 ? ` — version ${bon.version_no}` : ''}</span>
          <span>{LIBELLES_TYPE_BON[bon.type]} — {bon.site.nom}{bon.site.client ? ` (${bon.site.client})` : ''}</span>
          <BadgeStatutBon b={{ statut: bon.statut, remplace: !!bon.avenant && bon.avenant.statut === 'signe' }} />
          {bon.demo && <Badge ton="ambre" icone={<FlaskConical size={14} aria-hidden />}>Démo</Badge>}
        </span>} />

      {bon.motif_avenant && <Alerte ton="ambre" titre={`Avenant (version ${bon.version_no})`}>Motif : {bon.motif_avenant}</Alerte>}
      {bon.statut === 'signe' && (
        <Alerte ton="succes" titre={`Signé le ${dateHeure(bon.signature?.signe_le)}`}>
          Ce bon est verrouillé : il ne se modifie plus.{bon.avenant ? ' ' : ''}
          {bon.avenant && <Link to={`/interventions/bons/${bon.avenant.id}`}>Voir l’avenant (version {bon.avenant.version_no}, {bon.avenant.statut === 'signe' ? 'signé' : 'en cours'})</Link>}
        </Alerte>
      )}

      {bon.statut === 'signe' && <NotesApresSignature bon={bon} enLigne={enLigne} apres={rafraichir} />}

      {refusBon.length > 0 && (
        <Alerte ton="danger" titre="Modifications faites sans réseau non enregistrées">
          {refusBon[0].refus?.message ?? 'Le serveur a refusé l’enregistrement.'} Rien n’a été écrasé : la fiche montre la version du serveur.
          <span className="groupe-btn bi-reprise">
            <Bouton variante="principal" icone={<RotateCcw aria-hidden />} onClick={reprendre}>Reprendre mes modifications</Bouton>
            <Bouton variante="fantome" icone={<Trash2 aria-hidden />} onClick={abandonnerTout}>Abandonner</Bouton>
          </span>
        </Alerte>
      )}
      {!enLigne && bon.statut !== 'signe' && (
        <Alerte ton="ambre" titre={`Hors connexion — ${attenteBon.length ? `${modifications(attenteBon.length)} en attente d’envoi` : 'rien en attente'}`}>
          Ce bon reste lisible et modifiable : « Enregistrer » garde vos modifications sur ce téléphone ; elles partiront seules au retour du
          réseau. La signature, le PDF, les photos et les sauvegardes attendent le réseau.
        </Alerte>
      )}
      {enLigne && attenteBon.length > 0 && (
        <Alerte ton="info" titre={`${modifications(attenteBon.length)} en attente d’envoi`}>
          Faite{attenteBon.length > 1 ? 's' : ''} sans réseau, envoi automatique en cours.{' '}
          <Bouton compact variante="fantome" icone={<CloudUpload aria-hidden />} onClick={envoyerMaintenant}>Envoyer maintenant</Bouton>
        </Alerte>
      )}

      <fieldset className="bi-champs" disabled={!ecrire}>
        <legend className="visuellement-cache">Contenu du bon</legend>
        <Section titre="Intervention" ouvert compte={<Badge ton="neutre" icone={false}>{pluriel(f.techniciens.length, 'technicien')}</Badge>}>
          <div className="bi-grille">
            <Champ libelle="Titre" value={f.titre} onChange={(e) => maj({ titre: e.target.value })} maxLength={200} />
            <ChampChoix libelle="Type" value={f.type} onChange={(e) => maj({ type: e.target.value as TypeBon })} options={TYPES_BON.map((t) => ({ valeur: t, libelle: LIBELLES_TYPE_BON[t] }))} />
            <Champ libelle="Arrivée" type="datetime-local" value={f.arrivee} onChange={(e) => maj({ arrivee: e.target.value })} />
            <div className="bi-champ-action">
              <Champ libelle="Départ" type="datetime-local" value={f.depart} onChange={(e) => maj({ depart: e.target.value })} aide="Vide : l’heure de la signature." />
              {ecrire && <Bouton compact onClick={() => maj({ depart: versLocal(new Date().toISOString()) })}>Maintenant</Bouton>}
            </div>
            <Champ libelle="Demandeur" value={f.demandeur} onChange={(e) => maj({ demandeur: e.target.value })} maxLength={300} />
            {ecrire && (liens.data || enLigne) ? (
              <ChampChoix libelle="Équipement" value={f.equipmentId} onChange={(e) => maj({ equipmentId: e.target.value })} vide="Aucun en particulier"
                options={(liens.data?.equipements ?? []).map((x) => ({ valeur: x.id, libelle: `${x.label}${x.reference ? ` (${x.reference})` : ''}` }))} />
            ) : <Champ libelle="Équipement" value={bon.equipement ? bon.equipement.libelle : 'Aucun'} readOnly />}
            {ecrire && (liens.data || enLigne) ? (
              <ChampChoix libelle="Diagnostic lié" value={f.interventionId} onChange={(e) => maj({ interventionId: e.target.value })} vide="Aucun"
                options={(liens.data?.diagnostics ?? []).map((x) => ({ valeur: x.id, libelle: `${x.reference} — ${x.title}` }))} />
            ) : <Champ libelle="Diagnostic lié" value={bon.diagnostic ?? 'Aucun'} readOnly />}
            {ecrire && (liens.data || enLigne) ? (
              <ChampChoix libelle="Chantier lié" value={f.chantierId} onChange={(e) => maj({ chantierId: e.target.value })} vide="Aucun"
                options={(liens.data?.chantiers ?? []).map((x) => ({ valeur: x.id, libelle: `${x.reference ?? 'En préparation'} — ${x.title}` }))} />
            ) : <Champ libelle="Chantier lié" value={bon.chantier ?? 'Aucun'} readOnly />}
            {ecrire && (liens.data || enLigne) ? (
              <div className="bi-champ-action bi-devis-choix">
                <ChampChoix libelle="Devis accepté (au nom duquel on intervient)" value={f.devisRevisionId} vide="Aucun devis"
                  onChange={(e) => changerDevis(e.target.value)}
                  options={devisSite.map((x) => ({ valeur: x.revision_id,
                    libelle: `${libelleDevis(x)} — ${x.intitule}${f.chantierId && x.chantiers.includes(f.chantierId) ? ' (devis du chantier)' : ''}` }))} />
                {devisChantier && f.devisRevisionId !== devisChantier.revision_id && (
                  <Bouton compact onClick={() => changerDevis(devisChantier.revision_id)}>Devis du chantier</Bouton>
                )}
              </div>
            ) : <Champ libelle="Devis accepté" value={bon.devis ? `${libelleDevis(bon.devis)} — ${bon.devis.intitule}` : 'Aucun'} readOnly />}
          </div>
          {devisAffiche && (
            <p className="petit bi-devis-resume">
              Devis {libelleDevis(devisAffiche)} — {devisAffiche.intitule}{devisAffiche.accepte_le ? `, accepté par le client le ${date(devisAffiche.accepte_le)}` : ''}.
              {' '}<span className="discret">Le bon ne reprend aucun prix du devis.</span>
            </p>
          )}
          {bon.statut !== 'signe' && bon.devis && !bon.devis.accepte && f.devisRevisionId === bon.devis_revision_id && (
            <Alerte ton="ambre" titre="Devis plus accepté">L’acceptation de ce devis a été annulée ou remplacée : choisissez le devis accepté en vigueur avant de faire signer.</Alerte>
          )}
          <ChampTexte libelle="Objet de la demande" value={f.objet} onChange={(e) => maj({ objet: e.target.value })} rows={2} maxLength={4000} />
          <div className="champ">
            <span className="champ-libelle">Techniciens intervenus</span>
            {ecrire ? (
              <div className="bi-cases">
                {(intervenants.data ?? bon.techniciens).map((t) => (
                  <label key={t.id} className="case">
                    <input type="checkbox" checked={f.techniciens.includes(t.id)}
                      onChange={(e) => maj({ techniciens: e.target.checked ? [...f.techniciens, t.id] : f.techniciens.filter((x) => x !== t.id) })} />
                    <span>{t.nom}</span>
                  </label>
                ))}
              </div>
            ) : <p>{f.techniciens.map(tech).join(', ') || 'Non renseigné'}</p>}
          </div>
        </Section>

        <Section titre="Travaux réalisés et pièces" ouvert compte={<Compte n={s.pieces?.length ?? 0} un="pièce" />}>
          <ChampTexte libelle="Travaux réalisés" value={f.travaux} onChange={(e) => maj({ travaux: e.target.value })} rows={4} maxLength={8000}
            aide="Obligatoire pour faire signer." />
          <h3 className="bi-sous-titre">Pièces remplacées ou posées</h3>
          {(s.pieces ?? []).map((p, i) => (
            <div key={i} className="bi-ligne">
              <div className="bi-grille">
                <Champ libelle="Désignation" value={p.designation} onChange={(e) => majS({ pieces: s.pieces!.map((x, k) => (k === i ? { ...x, designation: e.target.value } : x)) })} maxLength={300} />
                <Champ libelle="Référence" mono value={p.reference ?? ''} onChange={(e) => majS({ pieces: s.pieces!.map((x, k) => (k === i ? { ...x, reference: e.target.value || null } : x)) })} maxLength={120} />
                <div className="bi-serie">
                  <Champ libelle="N° de série" mono value={p.numero_serie ?? ''} onChange={(e) => majS({ pieces: s.pieces!.map((x, k) => (k === i ? { ...x, numero_serie: e.target.value || null } : x)) })} maxLength={120} />
                  <SerieMaGamme bon={bon} numero={p.numero_serie} lien={lienGamme} />
                </div>
                <Champ libelle="Quantité" value={p.quantite ?? ''} onChange={(e) => majS({ pieces: s.pieces!.map((x, k) => (k === i ? { ...x, quantite: e.target.value || null } : x)) })} maxLength={30} inputMode="decimal" />
                <ChampChoix libelle="Mouvement" value={p.mouvement} onChange={(e) => majS({ pieces: s.pieces!.map((x, k) => (k === i ? { ...x, mouvement: e.target.value as any } : x)) })}
                  options={[{ valeur: 'posee', libelle: 'Posée' }, { valeur: 'remplacee', libelle: 'Remplacée' }, { valeur: 'deposee', libelle: 'Déposée' }]} />
              </div>
              {ecrire && <BoutonRetirer libelle={`Retirer la pièce ${i + 1}`} onClick={() => majS({ pieces: s.pieces!.filter((_, k) => k !== i) })} />}
            </div>
          ))}
          {ecrire && <Bouton icone={<Plus aria-hidden />} onClick={() => majS({ pieces: [...(s.pieces ?? []), { designation: '', reference: null, numero_serie: null, quantite: '1', mouvement: 'posee' }] })}>Ajouter une pièce</Bouton>}
        </Section>

        {(f.devisRevisionId || bon.devis) && (
          <Section titre="Ajouts / déductions au devis" id="bi-ajustements" ouvert={!!(s.ajustements_devis ?? []).length}
            compte={<Compte n={(s.ajustements_devis ?? []).length} un="ligne" />}>
            <AjustementsDevis lignes={s.ajustements_devis ?? []} ecrire={ecrire} changer={(l) => majS({ ajustements_devis: l })} />
          </Section>
        )}

        <Section titre="Passerelles et automates" compte={<Compte n={s.passerelles?.length ?? 0} un="passerelle" />}>
          <p className="petit discret">Passerelles KNX, Modbus, BACnet, M-Bus, DALI, LON, IP : adresse, firmware avant et après, numéro de série.</p>
          {(s.passerelles ?? []).map((p, i) => {
            const set = (v: Partial<typeof p>) => majS({ passerelles: s.passerelles!.map((x, k) => (k === i ? { ...x, ...v } : x)) });
            return (
              <div key={i} className="bi-ligne">
                <div className="pile">
                  <div className="bi-grille">
                    <Champ libelle="Passerelle ou automate" value={p.equipement} onChange={(e) => set({ equipement: e.target.value })} maxLength={200} />
                    <Champ libelle="Marque" value={p.marque ?? ''} onChange={(e) => set({ marque: e.target.value || null })} maxLength={120} />
                    <Champ libelle="Modèle" value={p.modele ?? ''} onChange={(e) => set({ modele: e.target.value || null })} maxLength={120} />
                    <div className="bi-serie">
                      <Champ libelle="N° de série" mono value={p.numero_serie ?? ''} onChange={(e) => set({ numero_serie: e.target.value || null })} maxLength={120} />
                      <SerieMaGamme bon={bon} numero={p.numero_serie} lien={lienGamme} />
                    </div>
                    <Champ libelle="Adresse (IP, KNX, ID Modbus, instance BACnet…)" mono value={p.adresse ?? ''} onChange={(e) => set({ adresse: e.target.value || null })} maxLength={300} />
                    <Champ libelle="Firmware avant" mono value={p.firmware_avant ?? ''} onChange={(e) => set({ firmware_avant: e.target.value || null })} maxLength={60} />
                    <Champ libelle="Firmware après" mono value={p.firmware_apres ?? ''} onChange={(e) => set({ firmware_apres: e.target.value || null })} maxLength={60} />
                  </div>
                  <div className="champ">
                    <span className="champ-libelle">Protocoles</span>
                    <div className="bi-cases">
                      {PROTOCOLES.map((pr) => (
                        <label key={pr} className="case"><input type="checkbox" checked={p.protocoles.includes(pr)}
                          onChange={(e) => set({ protocoles: e.target.checked ? [...p.protocoles, pr] : p.protocoles.filter((x) => x !== pr) })} /><span>{pr}</span></label>
                      ))}
                    </div>
                  </div>
                  <Champ libelle="Remarque" value={p.remarque ?? ''} onChange={(e) => set({ remarque: e.target.value || null })} maxLength={1000} />
                </div>
                {ecrire && <BoutonRetirer libelle={`Retirer la passerelle ${i + 1}`} onClick={() => majS({ passerelles: s.passerelles!.filter((_, k) => k !== i) })} />}
              </div>
            );
          })}
          {ecrire && <Bouton icone={<Plus aria-hidden />} onClick={() => majS({ passerelles: [...(s.passerelles ?? []), { equipement: '', marque: null, modele: null, protocoles: [], adresse: null, firmware_avant: null, firmware_apres: null, numero_serie: null, remarque: null }] })}>Ajouter une passerelle ou un automate</Bouton>}
        </Section>

        <Section titre="Points communiqués" compte={<Compte n={s.points?.length ?? 0} un="point" />}>
          {!!s.points?.length && <p className="petit">{['ok', 'ko', 'non_teste'].map((r) => `${s.points!.filter((p) => p.resultat === r).length} ${LIBELLES_RESULTAT[r as ResultatTest]}`).join(' · ')}</p>}
          {(s.points ?? []).map((p, i) => {
            const set = (v: Partial<typeof p>) => majS({ points: s.points!.map((x, k) => (k === i ? { ...x, ...v } : x)) });
            return (
              <div key={i} className="bi-ligne">
                <div className="pile">
                  <div className="bi-grille">
                    <Champ libelle="Repère (adresse de groupe, registre, objet)" mono value={p.repere} onChange={(e) => set({ repere: e.target.value })} maxLength={120} />
                    <Champ libelle="Libellé" value={p.libelle ?? ''} onChange={(e) => set({ libelle: e.target.value || null })} maxLength={300} />
                    <ChampChoix libelle="Lecture / écriture" value={p.acces ?? ''} onChange={(e) => set({ acces: (e.target.value || null) as any })} vide="Non précisé"
                      options={ACCES_POINT.map((a) => ({ valeur: a, libelle: LIBELLES_ACCES_POINT[a] }))} />
                    <Champ libelle="Valeur relevée" value={p.valeur ?? ''} onChange={(e) => set({ valeur: e.target.value || null })} maxLength={120} />
                  </div>
                  <Verdict nom={`Test du point ${p.repere || i + 1}`} valeur={p.resultat} changer={(r) => set({ resultat: r })} />
                </div>
                {ecrire && <BoutonRetirer libelle={`Retirer le point ${i + 1}`} onClick={() => majS({ points: s.points!.filter((_, k) => k !== i) })} />}
              </div>
            );
          })}
          {ecrire && <Bouton icone={<Plus aria-hidden />} onClick={() => majS({ points: [...(s.points ?? []), { repere: '', libelle: null, acces: null, valeur: null, resultat: 'non_teste' }] })}>Ajouter un point</Bouton>}
        </Section>

        <Section titre="Recette — PV d’essais" compte={<Compte n={s.essais?.length ?? 0} un="essai" />}>
          {(s.essais ?? []).map((x, i) => {
            const set = (v: Partial<typeof x>) => majS({ essais: s.essais!.map((y, k) => (k === i ? { ...y, ...v } : y)) });
            return (
              <div key={i} className="bi-ligne">
                <div className="pile">
                  <div className="bi-grille">
                    <Champ libelle="Essai fonctionnel" value={x.essai} onChange={(e) => set({ essai: e.target.value })} maxLength={300} />
                    <Champ libelle="Résultat attendu" value={x.attendu ?? ''} onChange={(e) => set({ attendu: e.target.value || null })} maxLength={500} />
                    <Champ libelle="Résultat obtenu" value={x.obtenu ?? ''} onChange={(e) => set({ obtenu: e.target.value || null })} maxLength={500} />
                  </div>
                  <Verdict nom={`Verdict de l’essai ${i + 1}`} valeur={x.resultat} changer={(r) => set({ resultat: r })} />
                </div>
                {ecrire && <BoutonRetirer libelle={`Retirer l’essai ${i + 1}`} onClick={() => majS({ essais: s.essais!.filter((_, k) => k !== i) })} />}
              </div>
            );
          })}
          {ecrire && <Bouton icone={<Plus aria-hidden />} onClick={() => majS({ essais: [...(s.essais ?? []), { essai: '', attendu: null, obtenu: null, resultat: 'non_teste' }] })}>Ajouter un essai</Bouton>}
          <ChampTexte libelle="Réserves" value={s.reserves ?? ''} onChange={(e) => majS({ reserves: e.target.value || null })} rows={2} maxLength={4000} />
        </Section>

        <Section titre="Sauvegarde du programme" compte={<Compte n={nbFichiers('sauvegarde')} un="sauvegarde" />}>
          <ListeFichiers bon={bon} nature="sauvegarde" enLigne={enLigne} />
          {ecrire && (enLigne ? <EnvoiFichier bon={bon} nature="sauvegarde" apres={rafraichir} /> : <p className="petit discret">Sauvegarde : à joindre au retour du réseau.</p>)}
        </Section>

        <Section titre="DOE — documents remis" compte={<Compte n={(s.doe ?? []).filter((x) => x.remis).length} un="remis" plusieurs="remis" />}>
          <div className="bi-cases bi-cases-colonne">
            {[...new Set([...(referentiels.data?.doe ?? DOE_PROPOSES), ...(s.doe ?? []).map((x) => x.document)])].map((doc) => {
              const actuel = (s.doe ?? []).find((x) => x.document === doc);
              return (
                <label key={doc} className="case"><input type="checkbox" checked={!!actuel?.remis}
                  onChange={(e) => majS({ doe: [...(s.doe ?? []).filter((x) => x.document !== doc), { document: doc, remis: e.target.checked }] })} /><span>{doc}</span></label>
              );
            })}
          </div>
          {ecrire && <AjoutDoe ajouter={(doc) => majS({ doe: [...(s.doe ?? []).filter((x) => x.document !== doc), { document: doc, remis: true }] })} />}
        </Section>

        <Section titre="Fluides frigorigènes (CVC)" compte={s.fluides ? <Badge ton="info" icone={false}>Fiche ouverte</Badge> : <Badge ton="neutre" icone={false}>Sans objet</Badge>}>
          {!s.fluides ? (
            <>
              <p className="petit discret">Pour une intervention sur un équipement contenant des fluides frigorigènes : rubriques de la fiche d’intervention (Cerfa 15497).</p>
              {ecrire && <Bouton icone={<FilePlus2 aria-hidden />} onClick={() => majS({ fluides: fluidesVides() })}>Ouvrir la fiche fluides</Bouton>}
            </>
          ) : <FicheFluides v={s.fluides} changer={(v) => majS({ fluides: v })} bon={bon} referentiels={referentiels.data} ecrire={ecrire} />}
        </Section>

        <Section titre="Photos" compte={<Compte n={nbFichiers('photo')} un="photo" />}>
          <ListeFichiers bon={bon} nature="photo" enLigne={enLigne} />
          {ecrire && (enLigne ? <EnvoiFichier bon={bon} nature="photo" apres={rafraichir} /> : <p className="petit discret">Photos : à joindre au retour du réseau.</p>)}
        </Section>

        <Section titre="Observations et suite à donner" ouvert={!!(f.observations || f.suiteADonner)}>
          <ChampTexte libelle="Observations" value={f.observations} onChange={(e) => maj({ observations: e.target.value })} rows={3} maxLength={8000} />
          <ChampTexte libelle="Suite à donner" value={f.suiteADonner} onChange={(e) => maj({ suiteADonner: e.target.value })} rows={2} maxLength={4000} />
        </Section>
      </fieldset>

      {bon.signature && (
        <section className="panneau" aria-labelledby="bi-sig">
          <div className="panneau-entete"><h2 id="bi-sig" style={{ fontSize: 'var(--t-l)' }}>Signatures</h2></div>
          <div className="panneau-corps pile">
            <div className="bi-signatures">
              <div>
                <p className="petit"><strong>Client</strong> — {bon.signature.client?.absent
                  ? `${bon.signature.client.absent === 'refus' ? 'refus de signer' : 'client absent'} (motif : ${bon.signature.client.motif})`
                  : `${bon.signature.client?.nom}, ${bon.signature.client?.qualite}`}</p>
                {bon.signature.client?.trace && <SignatureFigee trace={bon.signature.client.trace} libelle="Signature du client" />}
              </div>
              <div>
                <p className="petit"><strong>Technicien</strong> — {bon.signature.technicien?.nom}</p>
                {bon.signature.technicien && <SignatureFigee trace={bon.signature.technicien.trace} libelle="Signature du technicien" />}
              </div>
            </div>
            <p className="tres-petit discret">Signé le {dateHeure(bon.signature.signe_le)} (heure du serveur).</p>
            <p className="tres-petit"><ShieldCheck size={14} aria-hidden /> Empreinte SHA-256 du contenu signé : <span className="mono bi-empreinte">{bon.empreinte}</span></p>
          </div>
        </section>
      )}

      {bon.droits.supprimer && (
        <div className="groupe-btn">
          <Bouton variante="danger" icone={<Trash2 aria-hidden />} chargement={supprimer.isPending} disabled={!enLigne} title={enLigne ? undefined : 'Demande le réseau'}
            onClick={async () => { if (await confirmer('Supprimer ce brouillon ?', `Le bon ${bon.reference} et ses fichiers seront supprimés.`, 'Supprimer le brouillon')) supprimer.mutate(); }}>
            Supprimer ce brouillon</Bouton>
        </div>
      )}

      <div className="barre-actions barre-collante bi-barre">
        <span className="barre-collante-resume tres-petit">
          {bon.statut === 'signe' ? 'Bon signé et verrouillé.' : modifie ? 'Modifications non enregistrées.'
            : refusBon.length ? 'Modifications faites sans réseau refusées : voir en haut de la fiche.'
              : attenteBon.length ? 'Enregistré sur ce téléphone — envoi au retour du réseau.' : 'Tout est enregistré.'}
          {bon.droits.signer && signatureBloquee && <span className="bi-raison"> {signatureBloquee.court}</span>}
          {signatureBloquee && <span id="bi-signature-bloquee" className="visuellement-cache">{signatureBloquee.long}</span>}
        </span>
        {enLigne ? <a className="btn" href={`/api/bons/${bon.id}/pdf`} download><FileDown aria-hidden />PDF</a>
          : <Bouton icone={<FileDown aria-hidden />} disabled title="Le PDF demande le réseau">PDF</Bouton>}
        {ecrire && <Bouton icone={<Save aria-hidden />} chargement={enregistrer.isPending} disabled={!modifie || refusBon.length > 0}
          title={refusBon.length ? 'Reprenez ou abandonnez d’abord les modifications refusées (en haut de la fiche).' : undefined} onClick={() => enregistrer.mutate()}>Enregistrer</Bouton>}
        {bon.droits.signer && <Bouton variante="principal" icone={<FileSignature aria-hidden />} disabled={!!signatureBloquee} title={signatureBloquee?.long}
          aria-describedby={signatureBloquee ? 'bi-signature-bloquee' : undefined} onClick={ouvrirSignature}>Faire signer</Bouton>}
        {bon.droits.avenant && <Bouton icone={<FilePlus2 aria-hidden />} disabled={!enLigne} title={enLigne ? undefined : 'Demande le réseau'} onClick={() => setAvenant(true)}>Corriger par un avenant</Bouton>}
      </div>

      <DialogueSignature bon={bon} ouvert={signer} fermer={() => setSigner(false)} fini={(b) => { setSigner(false); setF(formDepuis(b)); rafraichir(b); notifier('Bon signé et verrouillé. Le PDF est prêt.'); }} />
      <DialogueAvenant bon={bon} ouvert={avenant} fermer={() => setAvenant(false)} />
      {confirmation}
    </div>
  );
}

// ─── Notes ajoutées après signature (migration 049) ───
// Le bon signé n'est jamais corrigé par le technicien : il AJOUTE une note à côté (date et heure du serveur, auteur). Réseau exigé.
function NotesApresSignature({ bon, enLigne, apres }: { bon: Bon; enLigne: boolean; apres: (b: Bon) => void }) {
  const notifier = useNotifier();
  const [texte, setTexte] = useState('');
  // Ajout ou déduction (051) : seulement si le bon signé cite un devis accepté ; la raison est le texte de la note.
  const [chiffre, setChiffre] = useState<'' | SensAjustement>('');
  const [montant, setMontant] = useState('');
  const notes = bon.notes ?? [];
  const bloquee = raisonNoteBloquee({ enLigne });
  const montantOk = !chiffre || centimesMontant(montant) !== null;
  const ajouter = useMutation({
    mutationFn: () => api.post<Bon>(`/api/bons/${bon.id}/notes`, chiffre ? { texte, sens: chiffre, montantHt: montant } : { texte }),
    onSuccess: (b) => { apres(b); setTexte(''); setChiffre(''); setMontant(''); notifier('Note ajoutée. Le bon signé n’a pas changé.'); },
    onError: (e) => notifier(messageErreur(e), true),
  });
  return (
    <section className="panneau bi-notes" aria-labelledby="bi-notes-titre">
      <div className="panneau-entete"><h2 id="bi-notes-titre" style={{ fontSize: 'var(--t-l)' }}>Notes ajoutées après signature</h2></div>
      <div className="panneau-corps pile">
        <p className="petit secondaire">Le bon signé n’est pas modifié : la note s’ajoute à côté, avec sa date et son auteur.</p>
        {notes.length === 0 ? <p className="petit discret">Aucune note pour l’instant.</p> : (
          <ol className="bi-notes-liste">
            {notes.map((n) => (
              <li key={n.id} className="bi-note">
                <p className="tres-petit discret">{dateHeure(n.le)} — {n.auteur ?? 'Auteur inconnu'}</p>
                {n.sens && n.montant_ht && (
                  <p className="petit bi-note-montant"><strong>{LIBELLES_SENS_AJUSTEMENT[n.sens]} au devis : {formatMontantSigne(centimesSignes({ sens: n.sens, montant_ht: n.montant_ht }))}</strong></p>
                )}
                <p className="petit bi-note-texte">{n.texte}</p>
              </li>
            ))}
          </ol>
        )}
        {notes.length > 0 && (enLigne ? <a className="btn bi-notes-pdf" href={`/api/bons/${bon.id}/notes/pdf`} download><FileDown aria-hidden />PDF des notes (à part du bon signé)</a> : null)}
        {bon.droits.noter && (
          <div className="pile">
            {bon.devis && (
              <div className="bi-grille">
                <ChampChoix libelle="Ajout ou déduction au devis" value={chiffre} vide="Aucun (note simple)" onChange={(e) => setChiffre(e.target.value as '' | SensAjustement)}
                  options={SENS_AJUSTEMENT.map((x) => ({ valeur: x, libelle: LIBELLES_SENS_AJUSTEMENT[x] }))} />
                {chiffre && <Champ libelle="Montant HT (€)" inputMode="decimal" value={montant} onChange={(e) => setMontant(e.target.value)}
                  erreur={montant && !montantOk ? 'Montant strictement positif, 2 décimales au plus.' : undefined} />}
              </div>
            )}
            <ChampTexte libelle={chiffre ? 'Raison de l’ajout ou de la déduction' : 'Nouvelle note'} value={texte} onChange={(e) => setTexte(e.target.value)} rows={3}
              maxLength={chiffre ? RAISON_AJUSTEMENT_MAX : 2000}
              aide={`${texte.trim().length} / ${chiffre ? '500' : '2 000'} caractères`} />
            <div className="groupe-btn">
              <Bouton variante="principal" icone={<MessageSquarePlus aria-hidden />} chargement={ajouter.isPending}
                disabled={!!bloquee || !texte.trim() || (!!chiffre && (!montant || !montantOk))} title={bloquee ?? undefined} aria-describedby={bloquee ? 'bi-note-bloquee' : undefined}
                onClick={() => ajouter.mutate()}>Ajouter la note</Bouton>
            </div>
            {bloquee && <p id="bi-note-bloquee" className="petit discret">{bloquee}</p>}
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Ajouts / déductions au devis (051) ───
// Le technicien les saisit sur place (raison + montant HT) ; c'est la SEULE exception à « le technicien ne voit aucun prix » et elle ne
// porte que sur les montants de CE bon. Le bon ne calcule pas le total du devis : seulement le total net de ces lignes.
function AjustementsDevis({ lignes, ecrire, changer }: { lignes: AjustementDevis[]; ecrire: boolean; changer: (l: AjustementDevis[]) => void }) {
  const set = (i: number, v: Partial<AjustementDevis>) => changer(lignes.map((x, k) => (k === i ? { ...x, ...v } : x)));
  const valides = lignes.filter((l) => centimesMontant(l.montant_ht) !== null);
  return (
    <div className="pile">
      <p className="petit discret">Seulement ce qui s’écarte du devis accepté : un rajout à faire ou une déduction, avec la raison et le montant HT. Le bon ne reprend aucun prix du devis.</p>
      {lignes.map((l, i) => {
        const invalide = !!l.montant_ht && centimesMontant(l.montant_ht) === null;
        return (
          <div key={i} className="bi-ligne bi-ajustement">
            <div className="pile">
              <div className="segment bi-sens" role="group" aria-label={`Sens de la ligne ${i + 1}`}>
                {SENS_AJUSTEMENT.map((x) => (
                  <button key={x} type="button" aria-pressed={l.sens === x} onClick={() => set(i, { sens: x })}>{LIBELLES_SENS_AJUSTEMENT[x]}</button>
                ))}
              </div>
              <div className="bi-grille">
                <Champ libelle="Raison" value={l.raison} onChange={(e) => set(i, { raison: e.target.value })} maxLength={RAISON_AJUSTEMENT_MAX} />
                <Champ libelle="Montant HT (€)" inputMode="decimal" value={String(l.montant_ht ?? '').replace('.', ',')} onChange={(e) => set(i, { montant_ht: e.target.value })}
                  erreur={invalide ? 'Montant strictement positif, 2 décimales au plus.' : undefined} />
              </div>
            </div>
            {ecrire && <BoutonRetirer libelle={`Retirer la ligne ${i + 1}`} onClick={() => changer(lignes.filter((_, k) => k !== i))} />}
          </div>
        );
      })}
      {lignes.length > 0 && (
        <p className="bi-ajustements-total"><strong>Total net : {formatMontantSigne(totalNetCentimes(valides))}</strong>
          {valides.length < lignes.length && <span className="petit discret"> (lignes au montant invalide non comptées)</span>}</p>
      )}
      {ecrire && (
        <div className="groupe-btn">
          <Bouton icone={<Plus aria-hidden />} onClick={() => changer([...lignes, { sens: 'ajout', raison: '', montant_ht: '' }])}>Ajouter un ajout</Bouton>
          <Bouton icone={<Plus aria-hidden />} onClick={() => changer([...lignes, { sens: 'deduction', raison: '', montant_ht: '' }])}>Ajouter une déduction</Bouton>
        </div>
      )}
    </div>
  );
}

function AjoutDoe({ ajouter }: { ajouter: (doc: string) => void }) {
  const [v, setV] = useState('');
  return (
    <div className="bi-champ-action">
      <Champ libelle="Autre document remis" value={v} onChange={(e) => setV(e.target.value)} maxLength={300} />
      <Bouton compact icone={<Plus aria-hidden />} disabled={!v.trim()} onClick={() => { ajouter(v.trim()); setV(''); }}>Ajouter</Bouton>
    </div>
  );
}

// ─── Fiche fluides ───
const nombreOuNull = (v: string) => (v.trim() === '' ? null : v.replace(',', '.'));
function FicheFluides({ v, changer, bon, referentiels, ecrire }: { v: FluidesBon; changer: (v: FluidesBon | null) => void; bon: Bon; referentiels?: Referentiels; ecrire: boolean }) {
  const set = <K extends keyof FluidesBon>(k: K, val: Partial<FluidesBon[K]> | FluidesBon[K]) =>
    changer({ ...v, [k]: typeof val === 'object' && val !== null && !Array.isArray(val) ? { ...(v[k] as any), ...val } : val });
  const kgTexte = (x: number | string | null) => (x === null || x === undefined ? '' : String(x).replace('.', ','));
  const calc = bon.fluides_calcul;
  const options = [
    ...(referentiels?.fluides.societe ?? []).map((x) => ({ valeur: `id:${x.id}`, libelle: `${x.name} (catalogue de la société)` })),
    ...(referentiels?.fluides.reference ?? []).flatMap((x) => (x.aliases[0] ? [{ valeur: `nom:${x.aliases[0]}`, libelle: `${x.aliases[0]} (${x.substance})` }] : [])),
  ];
  // L'opérateur (cadre 1 du Cerfa) est recopié par le serveur depuis l'intervenant : société (interne) ou sous-traitant choisi.
  const operateurAuto = v.intervenant?.type === 'interne' || (v.intervenant?.type === 'sous_traitant' && !!v.intervenant.sous_traitant_id);
  const choixFluide = v.equipement.fluide_id ? `id:${v.equipement.fluide_id}` : v.equipement.fluide ? `nom:${v.equipement.fluide}` : '';
  return (
    <div className="pile">
      <p className="petit discret">Rubriques de la fiche d’intervention ({calc?.cerfa ?? 'Cerfa n° 15497*04'}). Fiche à conserver 5 ans par l’opérateur et le détenteur.</p>
      <h3 className="bi-sous-titre">Intervenant fluides</h3>
      <ChampIntervenantFluides valeur={v.intervenant} ecrire={ecrire} changer={(i) => changer({ ...v, intervenant: i })}
        manque={bon.statut === 'signe' ? [] : bon.fluides_intervenant?.manque ?? []} avertissements={bon.fluides_intervenant?.avertissements ?? []} />
      <h3 className="bi-sous-titre">Opérateur et détenteur</h3>
      {operateurAuto && <p className="petit discret">Opérateur rempli d’après l’intervenant fluides, à l’enregistrement.</p>}
      <div className="bi-grille">
        <Champ libelle="Opérateur — nom" value={v.operateur.nom ?? ''} readOnly={operateurAuto} onChange={(e) => set('operateur', { nom: e.target.value || null })} maxLength={200} />
        <Champ libelle="N° d’attestation de capacité" mono value={v.operateur.attestation_capacite ?? ''} readOnly={operateurAuto} onChange={(e) => set('operateur', { attestation_capacite: e.target.value || null })} maxLength={80} />
        <Champ libelle="Opérateur — SIRET" mono value={v.operateur.siret ?? ''} readOnly={operateurAuto} onChange={(e) => set('operateur', { siret: e.target.value || null })} maxLength={20} />
        <Champ libelle="Opérateur — adresse" value={v.operateur.adresse ?? ''} readOnly={operateurAuto} onChange={(e) => set('operateur', { adresse: e.target.value || null })} maxLength={300} />
        <Champ libelle="Détenteur — nom" value={v.detenteur.nom ?? ''} onChange={(e) => set('detenteur', { nom: e.target.value || null })} maxLength={200} />
        <Champ libelle="Détenteur — SIRET (ou autre identifiant)" mono value={v.detenteur.siret ?? ''} onChange={(e) => set('detenteur', { siret: e.target.value || null })} maxLength={40} />
        <Champ libelle="Détenteur — adresse" value={v.detenteur.adresse ?? ''} onChange={(e) => set('detenteur', { adresse: e.target.value || null })} maxLength={300} />
      </div>
      <h3 className="bi-sous-titre">Équipement et fluide</h3>
      <div className="bi-grille">
        <Champ libelle="Identification de l’équipement" value={v.equipement.identification ?? ''} onChange={(e) => set('equipement', { identification: e.target.value || null })} maxLength={300} />
        <ChampChoix libelle="Fluide" value={choixFluide} vide="Choisir…" options={options}
          onChange={(e) => { const x = e.target.value; set('equipement', x.startsWith('id:') ? { fluide_id: x.slice(3), fluide: null } : { fluide_id: null, fluide: x ? x.slice(4) : null }); }} />
        <ChampChoix libelle="Famille réglementaire" value={v.equipement.famille ?? ''} vide="À préciser" onChange={(e) => set('equipement', { famille: (e.target.value || null) as any })}
          options={FAMILLES_FLUIDE.map((x) => ({ valeur: x, libelle: LIBELLES_FAMILLE_FLUIDE[x] }))} />
        <Champ libelle="Charge totale (kg)" inputMode="decimal" value={kgTexte(v.equipement.charge_totale_kg)} onChange={(e) => set('equipement', { charge_totale_kg: nombreOuNull(e.target.value) as any })} />
      </div>
      {calc && (
        <Alerte ton={calc.periodicite.mois ? 'info' : 'ambre'} titre="Contrôle d’étanchéité (calculé à l’enregistrement)">
          PRG : {v.equipement.prg ?? 'à vérifier (inconnu)'} — {calc.teq_co2 !== null ? `${String(calc.teq_co2).replace('.', ',')} t éq. CO2` : 'tonnage à vérifier'}.{' '}
          {calc.periodicite.mois ? `Fréquence minimale : tous les ${calc.periodicite.mois} mois (${calc.periodicite.classe} ; ${calc.periodicite.base}). Indicatif, à confirmer par l’opérateur.` : `À vérifier : ${calc.periodicite.classe}.`}
        </Alerte>
      )}
      <h3 className="bi-sous-titre">Nature de l’intervention</h3>
      <div className="bi-cases bi-cases-colonne">
        {NATURES_INTERVENTION_FLUIDE.map((n) => (
          <label key={n} className="case"><input type="checkbox" checked={v.natures.includes(n)}
            onChange={(e) => set('natures', e.target.checked ? [...v.natures, n] : v.natures.filter((x) => x !== n))} /><span>{LIBELLES_NATURE_FLUIDE[n]}</span></label>
        ))}
      </div>
      {v.natures.includes('autre') && <Champ libelle="Autre : préciser" value={v.nature_autre ?? ''} onChange={(e) => set('nature_autre', e.target.value || null)} maxLength={300} />}
      <h3 className="bi-sous-titre">Détection et fuites</h3>
      <div className="bi-grille">
        <Champ libelle="Détecteur manuel — identification" value={v.detecteur.identification ?? ''} onChange={(e) => set('detecteur', { identification: e.target.value || null })} maxLength={200} />
        <Champ libelle="Détecteur — date du dernier contrôle" type="date" value={v.detecteur.date_controle ?? ''} onChange={(e) => set('detecteur', { date_controle: e.target.value || null })} />
        <ChampChoix libelle="Système permanent de détection" value={v.detection_permanente === null ? '' : v.detection_permanente ? 'oui' : 'non'} vide="Non renseigné"
          onChange={(e) => set('detection_permanente', e.target.value === '' ? null : e.target.value === 'oui')} options={[{ valeur: 'oui', libelle: 'Oui' }, { valeur: 'non', libelle: 'Non' }]} />
        <ChampChoix libelle="Fuites constatées" value={v.fuites.constatees === null ? '' : v.fuites.constatees ? 'oui' : 'non'} vide="Non renseigné"
          onChange={(e) => set('fuites', { constatees: e.target.value === '' ? null : e.target.value === 'oui' })} options={[{ valeur: 'oui', libelle: 'Oui' }, { valeur: 'non', libelle: 'Non' }]} />
      </div>
      {v.fuites.constatees && (
        <>
          {v.fuites.lignes.map((l, i) => (
            <div key={i} className="bi-ligne">
              <div className="bi-grille">
                <Champ libelle={`Fuite ${i + 1} — localisation`} value={l.localisation} onChange={(e) => set('fuites', { lignes: v.fuites.lignes.map((x, k) => (k === i ? { ...x, localisation: e.target.value } : x)) })} maxLength={300} />
                <ChampChoix libelle="Réparation" value={l.reparation} onChange={(e) => set('fuites', { lignes: v.fuites.lignes.map((x, k) => (k === i ? { ...x, reparation: e.target.value as any } : x)) })}
                  options={[{ valeur: 'realisee', libelle: 'Réalisée' }, { valeur: 'a_faire', libelle: 'À faire' }]} />
              </div>
              {ecrire && <BoutonRetirer libelle={`Retirer la fuite ${i + 1}`} onClick={() => set('fuites', { lignes: v.fuites.lignes.filter((_, k) => k !== i) })} />}
            </div>
          ))}
          {ecrire && v.fuites.lignes.length < 3 && <Bouton icone={<Plus aria-hidden />} onClick={() => set('fuites', { lignes: [...v.fuites.lignes, { localisation: '', reparation: 'realisee' }] })}>Ajouter une fuite</Bouton>}
        </>
      )}
      <h3 className="bi-sous-titre">Quantités (kg)</h3>
      <div className="bi-grille">
        <Champ libelle="Chargé — vierge (A)" inputMode="decimal" value={kgTexte(v.charge.vierge_kg)} onChange={(e) => set('charge', { vierge_kg: nombreOuNull(e.target.value) as any })} />
        <Champ libelle="Chargé — recyclé (B)" inputMode="decimal" value={kgTexte(v.charge.recycle_kg)} onChange={(e) => set('charge', { recycle_kg: nombreOuNull(e.target.value) as any })} />
        <Champ libelle="Chargé — régénéré (C)" inputMode="decimal" value={kgTexte(v.charge.regenere_kg)} onChange={(e) => set('charge', { regenere_kg: nombreOuNull(e.target.value) as any })} />
        <Champ libelle="Récupéré — pour traitement (D)" inputMode="decimal" value={kgTexte(v.recupere.traitement_kg)} onChange={(e) => set('recupere', { traitement_kg: nombreOuNull(e.target.value) as any })} />
        <Champ libelle="Récupéré — pour réutilisation (E)" inputMode="decimal" value={kgTexte(v.recupere.reutilisation_kg)} onChange={(e) => set('recupere', { reutilisation_kg: nombreOuNull(e.target.value) as any })} />
        <Champ libelle="Contenant (identification)" mono value={v.recupere.contenant ?? ''} onChange={(e) => set('recupere', { contenant: e.target.value || null })} maxLength={120} />
        <Champ libelle="N° de BSFF (Trackdéchets)" mono value={v.recupere.bsff ?? ''} onChange={(e) => set('recupere', { bsff: e.target.value || null })} maxLength={60}
          aide="Saisi tel qu’il figure sur Trackdéchets (format non fixé par un texte : non contrôlé)." />
        <Champ libelle="Installation de destination du fluide récupéré" value={v.destination ?? ''} onChange={(e) => set('destination', e.target.value || null)} maxLength={400} />
      </div>
      <ChampTexte libelle="Observations (fluides)" value={v.observations ?? ''} onChange={(e) => set('observations', e.target.value || null)} rows={2} maxLength={2000} />
      {ecrire && <Bouton variante="fantome" icone={<X aria-hidden />} onClick={() => changer(null)}>Retirer la fiche fluides</Bouton>}
    </div>
  );
}

// ─── Fichiers ───
function ListeFichiers({ bon, nature, enLigne }: { bon: Bon; nature: 'photo' | 'sauvegarde'; enLigne: boolean }) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const l = bon.fichiers.filter((x) => x.nature === nature);
  const retirer = useMutation({
    mutationFn: (fid: string) => api.del<Bon>(`/api/bons/${bon.id}/fichiers/${fid}`),
    onSuccess: (b) => { qc.setQueryData(['bon', b.id], b); notifier('Fichier retiré.'); },
    onError: (e) => notifier(messageErreur(e), true),
  });
  if (!l.length) return <p className="petit discret">{nature === 'photo' ? 'Aucune photo.' : 'Aucune sauvegarde jointe.'}</p>;
  if (nature === 'photo') return (
    <div className="bi-photos">
      {l.map((x) => (
        <figure key={x.id} className="bi-photo">
          <img src={`/api/bons/${bon.id}/fichiers/${x.id}`} alt={x.legende || 'Photo de l’intervention'} loading="lazy" />
          <figcaption className="tres-petit">{x.legende || 'Sans légende'}</figcaption>
          {bon.droits.ecrire && enLigne && <BoutonRetirer libelle="Retirer cette photo" onClick={() => retirer.mutate(x.id)} />}
        </figure>
      ))}
    </div>
  );
  return (
    <ul className="bi-sauvegardes">
      {l.map((x: FichierBon) => (
        <li key={x.id} className="bi-ligne">
          <div className="pile" style={{ gap: 2, minWidth: 0 }}>
            <a href={`/api/bons/${bon.id}/fichiers/${x.id}`} download className="bi-lien-fichier">{x.file_name}</a>
            <span className="petit">{[x.legende, x.version_programme && `version ${x.version_programme}`, x.date_sauvegarde && `du ${x.date_sauvegarde.split('-').reverse().join('/')}`, x.emplacement && `rangée : ${x.emplacement}`].filter(Boolean).join(' — ') || 'Sans précision'}</span>
            <span className="tres-petit discret">SHA-256 (serveur) : <span className="mono bi-empreinte">{x.sha256}</span> — {x.taille} octets</span>
          </div>
          {bon.droits.ecrire && enLigne && <BoutonRetirer libelle={`Retirer ${x.file_name}`} onClick={() => retirer.mutate(x.id)} />}
        </li>
      ))}
    </ul>
  );
}

function EnvoiFichier({ bon, nature, apres }: { bon: Bon; nature: 'photo' | 'sauvegarde'; apres: (b: Bon) => void }) {
  const notifier = useNotifier();
  const [fichier, setFichier] = useState<File | null>(null);
  const [legende, setLegende] = useState(''); const [version, setVersion] = useState(''); const [date, setDate] = useState(''); const [emplacement, setEmplacement] = useState('');
  const [cle, setCle] = useState(0);
  const envoyer = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append('legende', legende);
      if (nature === 'sauvegarde') { fd.append('version', version); fd.append('date', date); fd.append('emplacement', emplacement); }
      fd.append('fichier', fichier!);
      return api.envoyer<Bon>(`/api/bons/${bon.id}/fichiers?nature=${nature}`, fd);
    },
    onSuccess: (b) => { apres(b); setFichier(null); setLegende(''); setVersion(''); setDate(''); setEmplacement(''); setCle((k) => k + 1); notifier(nature === 'photo' ? 'Photo ajoutée.' : 'Sauvegarde jointe, empreinte calculée.'); },
    onError: (e) => notifier(messageErreur(e), true),
  });
  return (
    <div className="pile bi-envoi">
      <label className="btn bi-choisir">
        {nature === 'photo' ? <Camera aria-hidden /> : <Upload aria-hidden />}
        {fichier ? fichier.name : nature === 'photo' ? 'Prendre ou choisir une photo' : 'Choisir le fichier de sauvegarde'}
        <input key={cle} type="file" className="visuellement-cache" accept={nature === 'photo' ? 'image/jpeg,image/png,image/webp' : undefined}
          capture={nature === 'photo' ? 'environment' : undefined} onChange={(e) => setFichier(e.target.files?.[0] ?? null)} />
      </label>
      {fichier && (
        <>
          <div className="bi-grille">
            <Champ libelle={nature === 'photo' ? 'Légende' : 'Équipement concerné'} value={legende} onChange={(e) => setLegende(e.target.value)} maxLength={300} />
            {nature === 'sauvegarde' && <>
              <Champ libelle="Version du programme" mono value={version} onChange={(e) => setVersion(e.target.value)} maxLength={120} />
              <Champ libelle="Date de la sauvegarde" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              <Champ libelle="Emplacement (où elle est rangée)" value={emplacement} onChange={(e) => setEmplacement(e.target.value)} maxLength={300} />
            </>}
          </div>
          <Bouton variante="principal" icone={<Upload aria-hidden />} chargement={envoyer.isPending} onClick={() => envoyer.mutate()}>
            {nature === 'photo' ? 'Ajouter la photo' : 'Joindre la sauvegarde'}</Bouton>
        </>
      )}
    </div>
  );
}

// ─── Signature ───
function DialogueSignature({ bon, ouvert, fermer, fini }: { bon: Bon; ouvert: boolean; fermer: () => void; fini: (b: Bon) => void }) {
  const notifier = useNotifier();
  const [mode, setMode] = useState<'signe' | 'absent' | 'refus'>('signe');
  const [nom, setNom] = useState(''); const [qualite, setQualite] = useState(''); const [motif, setMotif] = useState('');
  const [client, setClient] = useState<TraceSignature | null>(null);
  const [technicien, setTechnicien] = useState<TraceSignature | null>(null);
  useEffect(() => { if (ouvert) { setClient(null); setTechnicien(null); } }, [ouvert]);
  const pret = !!technicien && (mode === 'signe' ? !!client && !!nom.trim() && !!qualite.trim() : !!motif.trim());
  const signer = useMutation({
    mutationFn: () => api.post<Bon>(`/api/bons/${bon.id}/signer`, {
      client: mode === 'signe' ? { nom, qualite, trace: client } : { absent: mode, motif, nom: nom || null, qualite: qualite || null },
      technicien: { trace: technicien },
    }),
    onSuccess: fini,
    onError: (e) => notifier(messageErreur(e) + (e instanceof ErreurApi && Array.isArray(e.details) ? ` ${(e.details as any[]).map((x) => x.message).join(', ')}` : ''), true),
  });
  return (
    <Dialogue titre={`Faire signer ${bon.reference}`} ouvert={ouvert} fermer={fermer} classe="bi-dialogue-signature"
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" icone={<FileSignature aria-hidden />} disabled={!pret} chargement={signer.isPending} onClick={() => signer.mutate()}>Signer et verrouiller</Bouton></>}>
      <div className="pile">
        <div className="segment bi-segment-signature" role="group" aria-label="Signature du client">
          <button type="button" aria-pressed={mode === 'signe'} onClick={() => setMode('signe')}>Le client signe</button>
          <button type="button" aria-pressed={mode === 'absent'} onClick={() => setMode('absent')}>Client absent</button>
          <button type="button" aria-pressed={mode === 'refus'} onClick={() => setMode('refus')}>Refus de signer</button>
        </div>
        {mode === 'signe' ? (
          <>
            <div className="bi-grille">
              <Champ libelle="Nom du signataire" value={nom} onChange={(e) => setNom(e.target.value)} maxLength={200} autoComplete="off" />
              <Champ libelle="Qualité (fonction)" value={qualite} onChange={(e) => setQualite(e.target.value)} maxLength={200} placeholder="Responsable technique, gardien…" />
            </div>
            <ZoneSignature libelle="Signature du client" valeur={client} changer={setClient} />
          </>
        ) : (
          <ChampTexte libelle={mode === 'refus' ? 'Motif du refus' : 'Motif (client absent)'} value={motif} onChange={(e) => setMotif(e.target.value)} rows={2} maxLength={1000} />
        )}
        <ZoneSignature libelle="Signature du technicien" valeur={technicien} changer={setTechnicien} />
        <p className="tres-petit discret">Date et heure : celles du serveur au moment de la signature. Une fois signé, le bon est verrouillé ; une correction passera par un avenant.</p>
      </div>
    </Dialogue>
  );
}

function DialogueAvenant({ bon, ouvert, fermer }: { bon: Bon; ouvert: boolean; fermer: () => void }) {
  const nav = useNavigate(); const qc = useQueryClient(); const notifier = useNotifier();
  const [motif, setMotif] = useState('');
  const creer = useMutation({
    mutationFn: () => api.post<Bon>(`/api/bons/${bon.id}/avenant`, { motif }),
    onSuccess: (b) => { qc.invalidateQueries({ queryKey: ['bons'] }); qc.invalidateQueries({ queryKey: ['bon', bon.id] }); fermer(); notifier(`Avenant (version ${b.version_no}) créé.`); nav(`/interventions/bons/${b.id}`); },
    onError: (e) => notifier(messageErreur(e), true),
  });
  return (
    <Dialogue titre="Corriger par un avenant" ouvert={ouvert} fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" disabled={motif.trim().length < 3} chargement={creer.isPending} onClick={() => creer.mutate()}>Créer l’avenant</Bouton></>}>
      <p className="petit">Le bon signé reste tel quel. L’avenant reprend son contenu dans une nouvelle version (même référence), à corriger puis à faire signer.</p>
      <ChampTexte libelle="Motif de la correction" value={motif} onChange={(e) => setMotif(e.target.value)} rows={3} maxLength={2000} />
    </Dialogue>
  );
}
